import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

function buildServerPublicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (
          isNewSupabaseApiKey(key) &&
          headers.get("Authorization") === `Bearer ${key}`
        ) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export type PublicDeliveryAreaDTO = {
  id: string;
  neighbourhood: string;
  areaGroup: string;
  notes: string;
  sortOrder: number;
};

/**
 * Public unauthenticated read of active, non-demo delivery areas.
 * Returns null on ANY database error so the caller can fall back to
 * bundled sample copy (help text only — never used to answer availability).
 */
export const listActiveDeliveryAreas = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicDeliveryAreaDTO[] | null> => {
    try {
      const supabase = buildServerPublicClient();
      const { data, error } = await supabase
        .from("delivery_areas")
        .select("id, neighbourhood, area_group, notes, sort_order")
        .eq("is_active", true)
        .eq("is_demo", false)
        .order("sort_order", { ascending: true });
      if (error) {
        console.error("[delivery-areas] read failed", error);
        return null;
      }
      return (data ?? []).map((r) => ({
        id: r.id,
        neighbourhood: r.neighbourhood,
        areaGroup: r.area_group,
        notes: r.notes,
        sortOrder: r.sort_order,
      }));
    } catch (err) {
      console.error("[delivery-areas] unexpected error", err);
      return null;
    }
  },
);